import Input from 'components/RouteWizard/Input'
import { Select } from 'components/RouteWizard/Select'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { clientsAPI } from '../../../api/clientsAPI'
import { BookingReference } from '../../../api/models/BookingReference.type'
import { ReferenceType } from '../../../app/constants/BookingReference/BookingReferenceConstants'
import { Passenger } from '../AccountInformation/AdditionalPassengerDetails'

const BOOKING_REFERENCE_VALUES_SEARCH_PAGE_SIZE = 100

const getPagedBookingReferenceValues = (payload: any) => {
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.value)) return payload.value
  if (Array.isArray(payload?.values)) return payload.values
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  if (Array.isArray(payload?.data?.value)) return payload.data.value
  return []
}

const getPagedBookingReferenceHasMore = (
  payload: any,
  page: number,
  limit: number,
  resultCount: number,
) => {
  const explicitHasMore =
    payload?.hasMore ??
    payload?.pagination?.hasMore ??
    payload?.meta?.hasMore ??
    payload?.pageInfo?.hasMore

  if (typeof explicitHasMore === 'boolean') {
    return explicitHasMore
  }

  const total =
    payload?.totalSize ??
    payload?.totalCount ??
    payload?.total ??
    payload?.count ??
    payload?.pagination?.totalSize ??
    payload?.pagination?.totalCount ??
    payload?.pagination?.total ??
    payload?.meta?.totalSize ??
    payload?.meta?.totalCount

  if (typeof total === 'number') {
    return page * limit < total
  }

  // Fallback when the endpoint doesn't return pagination metadata.
  return resultCount >= limit
}

interface BookingReferenceProps {
  item: BookingReference
  index: number
  useLeadPassengerDefaults?: boolean
  leadPassenger?: Passenger
}

export default function BookingReferenceItem({
  item,
  index,
  useLeadPassengerDefaults = false,
  leadPassenger,
}: BookingReferenceProps) {
  const currentUser = useSelector((state: any) => state.users.user)
  const {
    register,
    setValue,
    getValues,
    formState: { touchedFields, dirtyFields },
  } = useFormContext()

  const fieldPath = `bookingReferenceValues[${index}].value`
  const isUserProperty = item.defaultValueType === 'UserProperty'
  const hasMounted = useRef(false)
  const wasFieldEdited = Boolean(
    touchedFields.bookingReferenceValues?.[index]?.value ||
      dirtyFields.bookingReferenceValues?.[index]?.value,
  )

  const getDefault = () => {
    if (!isUserProperty) return item.defaultValue ?? ''
    const src =
      useLeadPassengerDefaults && leadPassenger ? leadPassenger : currentUser
    return (
      src?.userProperties?.find(
        (p: any) => p.userPropertyId === item.userPropertyId,
      )?.value ?? ''
    )
  }

  const getNormalisedDefault = () => {
    const defaultValue = getDefault()

    if (
      item.referenceType === ReferenceType.ARRAY &&
      (typeof defaultValue === 'boolean' || typeof defaultValue === 'number')
    ) {
      return String(defaultValue)
    }

    return defaultValue ?? ''
  }

  // 1) on mount, register metadata + set initial default if empty
  useEffect(() => {
    // metadata only
    register(`bookingReferenceValues[${index}].bookingReferenceId`)
    register(`bookingReferenceValues[${index}].isMandatory`)
    register(`bookingReferenceValues[${index}].regex`)

    setValue(`bookingReferenceValues[${index}].bookingReferenceId`, item.id)
    setValue(`bookingReferenceValues[${index}].isMandatory`, item.isMandatory)
    setValue(`bookingReferenceValues[${index}].regex`, item.regex)

    // initial default
    const cur = getValues(fieldPath)
    if ((cur === undefined || cur === '') && !wasFieldEdited) {
      setValue(fieldPath, getNormalisedDefault())
    }
  }, [index, item, register, setValue, getValues, fieldPath, wasFieldEdited])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    if (!isUserProperty) return

    if (!wasFieldEdited) {
      setValue(fieldPath, getNormalisedDefault())
    }
  }, [leadPassenger, useLeadPassengerDefaults, wasFieldEdited])

  // your custom Input/Select already does its own internal Controller/register
  if (item.referenceType === ReferenceType.FREE_FORMAT) {
    return <Input name={fieldPath} label={item.title} autoComplete="off" />
  }

  if (item.referenceType === ReferenceType.ARRAY) {
    const hasRemoteValuesEndpoint = Boolean(item.clientId && item.id)

    const options: Record<string, string> = {}
    if (!hasRemoteValuesEndpoint) {
      item.values?.forEach((v) => {
        options[v] = v
      })
    }

    return (
      <Select
        name={fieldPath}
        label={item.title}
        options={hasRemoteValuesEndpoint ? undefined : options}
        searchable
        clearable
        pageSize={BOOKING_REFERENCE_VALUES_SEARCH_PAGE_SIZE}
        loadOptions={
          hasRemoteValuesEndpoint
            ? async ({
                search,
                page,
                limit,
              }: {
                search: string
                page: number
                limit: number
              }) => {
                const trimmedSearch = search?.trim() ?? ''
                const effectiveLimit = limit
                const offset = (page - 1) * effectiveLimit

                const response = await clientsAPI.getBookingReferenceValues(
                  item.clientId,
                  item.id,
                  {
                    limit: effectiveLimit,
                    offset,
                    ...(trimmedSearch ? { search: trimmedSearch } : {}),
                  },
                )

                const values = getPagedBookingReferenceValues(response?.data)
                const normalisedOptions = values
                  .map((value: any) => {
                    if (value === null || value === undefined) return null

                    if (
                      typeof value === 'string' ||
                      typeof value === 'number'
                    ) {
                      return { value, label: value }
                    }

                    const optionValue =
                      value?.value ?? value?.id ?? value?.key ?? value?.code
                    const optionLabel =
                      value?.label ??
                      value?.name ??
                      value?.value ??
                      value?.displayValue ??
                      optionValue

                    if (
                      optionValue === null ||
                      optionValue === undefined ||
                      optionLabel === null ||
                      optionLabel === undefined
                    ) {
                      return null
                    }

                    return {
                      value: optionValue,
                      label: optionLabel,
                    }
                  })
                  .filter(Boolean)

                return {
                  options: normalisedOptions as Array<{
                    value: string | number
                    label: string | number
                  }>,
                  hasMore: getPagedBookingReferenceHasMore(
                    response?.data,
                    page,
                    limit,
                    values.length,
                  ),
                }
              }
            : undefined
        }
      />
    )
  }

  return null
}
